import { createContext, useContext, useEffect, useMemo, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

function getSystemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyThemeClass(theme: Theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? null;
  if (stored === "light" || stored === "dark") return stored;
  return getSystemPrefersDark() ? "dark" : "light";
}

type ThemeContextValue = {
  theme: Theme;
  isDark: boolean;
  setTheme: (t: Theme) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    applyThemeClass(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    isDark: theme === "dark",
    setTheme,
    toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")),
  }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback to local state if provider is missing (avoids hard crash)
    const [theme, setTheme] = useState<Theme>(getInitialTheme);
    useEffect(() => {
      applyThemeClass(theme);
      try {
        localStorage.setItem(STORAGE_KEY, theme);
      } catch {}
    }, [theme]);
    const isDark = theme === "dark";
    const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
    return { theme, isDark, setTheme, toggle } as ThemeContextValue;
  }
  return ctx;
}
